import { useEffect, useMemo, useState, useCallback } from 'react'
import useSWR from 'swr'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import { Countries, Products } from '@/comm'
import { formatNumber, formatPercent } from '@/lib/format-utils'
import { exportToCsv } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DropdownMediaGroup } from '@/components/DropdownMediaGroup'
import { MyDatePicker } from '@/components/MyDatePicker'
import dayjs from 'dayjs'
import { create } from 'zustand'
import { useNavigate } from 'react-router-dom'
import { useDay0RoasDatePicker } from './stores/dayn-roas-date-store'

// DAY_OPTIONS moved to constant to share or keep local if needed
const DAY_OPTIONS = [
  { label: 'Day 0', value: '0' },
  { label: 'Day 3', value: '3' },
  { label: 'Day 7', value: '7' },
  { label: 'Day 14', value: '14' },
  { label: 'Day 30', value: '30' },
  { label: 'Day 60', value: '60' },
  { label: 'Day 90', value: '90' },
  { label: 'Day 120', value: '120' },
  { label: 'Day 150', value: '150' },
  { label: 'Day 180', value: '180' },
]

// Country and PID columns similar to ER page
const countryColumn = {
  id: 'country',
  header: 'Country',
  accessorFn: (row) => row.country,
  cell: (row) => {
    const country = row.getValue()
    const isTotal = country === 'total'
    return (
      <div className={cn('font-medium')}>
        {isTotal ? 'Total' : country.toUpperCase()}
      </div>
    )
  },
  meta: {
    sticky: true,
    stickyLeft: 'left-[0px]',
  },
}

// 汇总多个 package_name 的数据（用于 pid=14 等特殊情况）
const aggregateRoasData = (dataArray) => {
  if (!dataArray || dataArray.length === 0) return null
  if (dataArray.length === 1) return dataArray[0]

  const totalRevenue = dataArray.reduce((sum, d) => sum + d.total_revenue, 0)
  const totalCost = dataArray.reduce((sum, d) => sum + d.total_cost, 0)
  const avgRoas = totalCost > 0 ? totalRevenue / totalCost : 0

  const totalDays = dataArray.reduce((sum, d) => sum + d.days_count, 0)
  const uniqueDays = new Set(dataArray.map((d) => d.days_count)).size

  const dailyRevenue =
    uniqueDays > 0 ? totalRevenue / (totalDays / dataArray.length) : 0
  const dailyCost =
    uniqueDays > 0 ? totalCost / (totalDays / dataArray.length) : 0

  return {
    avg_day0_roas: avgRoas,
    daily_revenue: dailyRevenue,
    daily_cost: dailyCost,
    total_revenue: totalRevenue,
    total_cost: totalCost,
    days_count: totalDays,
  }
}

const pidColumn = (pid, data, onCellClick) => ({
  id: `pid-day0-roas-${pid.code}`,
  header: `${pid.name}`,
  meta: {
    pidCode: parseInt(pid.code),
    onCellClick, // 将点击处理函数存储在 meta 中
  },
  accessorFn: (row) => {
    if (row.country === 'total') {
      // Calculate total for this PID across all countries
      const pidData = data.filter((d) => d.pid === parseInt(pid.code))
      if (pidData.length === 0) return null

      const totalRevenue = pidData.reduce((sum, d) => sum + d.total_revenue, 0)
      const totalCost = pidData.reduce((sum, d) => sum + d.total_cost, 0)
      const avgRoas = totalCost > 0 ? totalRevenue / totalCost : 0

      const totalDays = pidData.reduce((sum, d) => sum + d.days_count, 0)
      const uniqueDays = new Set(pidData.map((d) => d.days_count)).size

      const dailyRevenue =
        uniqueDays > 0 ? totalRevenue / (totalDays / pidData.length) : 0
      const dailyCost =
        uniqueDays > 0 ? totalCost / (totalDays / pidData.length) : 0

      return {
        avg_day0_roas: avgRoas,
        daily_revenue: dailyRevenue,
        daily_cost: dailyCost,
        isTotal: true,
      }
    } else {
      // 对于 pid=14 等特殊情况，可能有多个 package_name，需要汇总所有数据
      const pidCode = parseInt(pid.code)
      const matchedData = data.filter(
        (d) => d.country === row.country && d.pid === pidCode,
      )

      // 如果有多条数据（多个 package_name），需要汇总
      if (matchedData.length > 1) {
        return aggregateRoasData(matchedData)
      }

      // 如果只有一条数据，直接返回
      return matchedData.length === 1 ? matchedData[0] : null
    }
  },
  cell: (context) => {
    const roasData = context.getValue()
    const row = context.row
    if (!roasData) {
      return <div className="text-gray-400 text-center">-</div>
    }

    const roas = roasData.avg_day0_roas
    const dailyRevenue = roasData.daily_revenue
    const dailyCost = roasData.daily_cost
    const isTotal = roasData.isTotal
    const pidCode = parseInt(pid.code)

    // 从 row.original 获取 country
    const country = row.original?.country

    // 调试：检查 country 获取方式
    // if (pid.code === '14' || pidCode === 14) {
    //   console.log('PID 14 cell rendering - country detection:', {
    //     pidCode,
    //     countryFromOriginal: row.original?.country,
    //     country: country,
    //     isTotal,
    //   })
    // }

    // 安全检查：确保 country 存在且 data 存在
    if (!country || !data || !Array.isArray(data)) {
      // 调试：如果是 PID 14，记录为什么提前返回
      // if (pid.code === '14' || pidCode === 14) {
      //   console.log('PID 14 cell early return:', {
      //     pidCode,
      //     country,
      //     hasCountry: !!country,
      //     hasData: !!data,
      //     isArray: Array.isArray(data),
      //   })
      // }
      return (
        <div className="text-right">
          <div
            className={cn(
              'font-bold',
              isTotal
                ? 'text-blue-800'
                : roas >= 1.0
                ? 'text-green-600'
                : roas >= 0.5
                ? 'text-yellow-600'
                : 'text-red-600',
            )}
          >
            {formatPercent(roas, 1)}
          </div>
          <div className="text-xs text-gray-500">
            ${formatNumber(dailyRevenue, 0)} / ${formatNumber(dailyCost, 0)}
          </div>
        </div>
      )
    }

    // 判断是否为 pid=14, country=JP，如果是则添加点击事件
    // 注意：country 可能是小写，需要统一处理
    const normalizedCountry = country?.toUpperCase()
    const isClickable = pidCode === 14 && normalizedCountry === 'JP' && !isTotal

    // 获取该 country 和 pid 的所有原始数据（包含所有 package_name）
    const matchedData = data.filter(
      (d) => d.country === country && d.pid === pidCode,
    )
    const uniquePackageNames = new Set(
      matchedData.map((d) => d.packageName).filter(Boolean),
    )
    const hasMultiplePackages = uniquePackageNames.size > 1
    // 对于 pid=14, country=JP，只要有数据就允许点击（即使只有一个 package_name）
    const canClick = isClickable && matchedData.length > 0

    // 调试信息：仅打印 pidCode=14 的日志
    // if (pidCode === 14) {
    //   console.log('PID 14 cell check:', {
    //     pidCode,
    //     country,
    //     normalizedCountry,
    //     isTotal,
    //     isClickable,
    //     matchedDataLength: matchedData.length,
    //     matchedData: matchedData,
    //     uniquePackageNames: Array.from(uniquePackageNames),
    //     canClick,
    //     hasMultiplePackages,
    //     hasOnCellClick: !!onCellClick,
    //   })
    // }

    // 不要修改 columnDef.meta，因为它是列共享的
    // 我们直接在返回的 JSX 中处理点击事件

    const handleClick = (e) => {
      // 必须立即阻止事件冒泡，防止 TableRow 的 onClick 触发
      e.stopPropagation()
      e.preventDefault()

      if (canClick && onCellClick) {
        onCellClick()
      }
    }

    const handleMouseDown = (e) => {
      // 在 mousedown 时也阻止，确保事件不会传播
      if (canClick) {
        e.stopPropagation()
      }
    }

    // 如果可点击，返回一个可点击的包装器
    // 添加 data 属性，让 TableRow 知道这是可点击的 cell
    if (canClick) {
      return (
        <div className="text-right relative cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors">
          {/* 右上角小绿点标记 */}
          <div
            className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full shadow-md z-10"
            style={{ pointerEvents: 'none' }}
            title="点击查看详细数据"
          />
          <div
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            data-clickable-cell="true"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClick(e)
              }
            }}
            title={
              hasMultiplePackages
                ? '点击查看按 Package Name 分组的详细数据'
                : '点击查看详细数据'
            }
          >
            <div
              className={cn(
                'font-bold',
                isTotal
                  ? 'text-blue-800'
                  : roas >= 1.0
                  ? 'text-green-600'
                  : roas >= 0.5
                  ? 'text-yellow-600'
                  : 'text-red-600',
              )}
            >
              {formatPercent(roas, 1)}
            </div>
            <div className="text-xs text-gray-500">
              ${formatNumber(dailyRevenue, 0)} / ${formatNumber(dailyCost, 0)}
            </div>
          </div>
        </div>
      )
    }

    // 不可点击时返回普通显示
    return (
      <div className="text-right">
        <div
          className={cn(
            'font-bold',
            isTotal
              ? 'text-blue-800'
              : roas >= 1.0
              ? 'text-green-600'
              : roas >= 0.5
              ? 'text-yellow-600'
              : 'text-red-600',
          )}
        >
          {formatPercent(roas, 1)}
        </div>
        <div className="text-xs text-gray-500">
          ${formatNumber(dailyRevenue, 0)} / ${formatNumber(dailyCost, 0)}
        </div>
      </div>
    )
  },
})

const totalColumn = (data) => ({
  id: 'total-roas',
  header: 'Total',
  accessorFn: (row) => {
    if (row.country === 'total') {
      const totalRevenue = data.reduce((sum, d) => sum + d.total_revenue, 0)
      const totalCost = data.reduce((sum, d) => sum + d.total_cost, 0)
      const avgRoas = totalCost > 0 ? totalRevenue / totalCost : 0

      const totalDays = data.reduce((sum, d) => sum + d.days_count, 0)
      const uniqueDays = new Set(data.map((d) => d.days_count)).size

      const dailyRevenue =
        uniqueDays > 0 ? totalRevenue / (totalDays / data.length) : 0
      const dailyCost =
        uniqueDays > 0 ? totalCost / (totalDays / data.length) : 0

      return {
        avg_day0_roas: avgRoas,
        daily_revenue: dailyRevenue,
        daily_cost: dailyCost,
        isTotal: true,
      }
    } else {
      const countryData = data.filter((d) => d.country === row.country)
      if (countryData.length === 0) return null

      const totalRevenue = countryData.reduce(
        (sum, d) => sum + d.total_revenue,
        0,
      )
      const totalCost = countryData.reduce((sum, d) => sum + d.total_cost, 0)
      const avgRoas = totalCost > 0 ? totalRevenue / totalCost : 0

      const totalDays = countryData.reduce((sum, d) => sum + d.days_count, 0)
      const uniqueDays = new Set(countryData.map((d) => d.days_count)).size

      const dailyRevenue =
        uniqueDays > 0 ? totalRevenue / (totalDays / countryData.length) : 0
      const dailyCost =
        uniqueDays > 0 ? totalCost / (totalDays / countryData.length) : 0

      return {
        avg_day0_roas: avgRoas,
        daily_revenue: dailyRevenue,
        daily_cost: dailyCost,
      }
    }
  },
  cell: (row) => {
    const roasData = row.getValue()
    if (!roasData) {
      return <div className="text-gray-400 text-center">-</div>
    }

    const roas = roasData.avg_day0_roas
    const dailyRevenue = roasData.daily_revenue
    const dailyCost = roasData.daily_cost
    const isTotal = roasData.isTotal

    return (
      <div className="text-right">
        <div
          className={cn(
            'font-bold',
            isTotal
              ? 'text-blue-800'
              : roas >= 1.0
              ? 'text-green-600'
              : roas >= 0.5
              ? 'text-yellow-600'
              : 'text-red-600',
          )}
        >
          {formatPercent(roas, 1)}
        </div>
        <div className="text-xs text-gray-500">
          ${formatNumber(dailyRevenue, 0)} / ${formatNumber(dailyCost, 0)}
        </div>
      </div>
    )
  },
})

const Day0RoasPage = () => {
  const navigate = useNavigate()
  const [mediaSource, setMediaSource] = useState('total')
  const [selectedDay, setSelectedDay] = useState('0')
  const [customDayInput, setCustomDayInput] = useState('')
  const [isDaySelectOpen, setIsDaySelectOpen] = useState(false)
  const startDate = useDay0RoasDatePicker((state) => state.startDate)
  const endDate = useDay0RoasDatePicker((state) => state.endDate)
  const updateStartDate = useDay0RoasDatePicker(
    (state) => state.updateStartDate,
  )
  const updateEndDate = useDay0RoasDatePicker((state) => state.updateEndDate)

  const selectOptions = useMemo(() => {
    const exists = DAY_OPTIONS.some((option) => option.value === selectedDay)
    if (exists) {
      return DAY_OPTIONS
    }
    return [...DAY_OPTIONS, { label: `Day ${selectedDay}`, value: selectedDay }]
  }, [selectedDay])

  const dayValue = useMemo(() => Number(selectedDay), [selectedDay])

  const maxAllowedEndDay = useMemo(
    () => dayjs().subtract(dayValue + 2, 'day'),
    [dayValue],
  )

  const maxAllowedEndDateStr = useMemo(
    () => maxAllowedEndDay.format('YYYYMMDD'),
    [maxAllowedEndDay],
  )

  useEffect(() => {
    if (!startDate || !endDate) {
      return
    }

    const parsedStart = dayjs(startDate, 'YYYYMMDD', true)
    const parsedEnd = dayjs(endDate, 'YYYYMMDD', true)

    if (!parsedStart.isValid() || !parsedEnd.isValid()) {
      return
    }

    if (!parsedEnd.isAfter(maxAllowedEndDay)) {
      return
    }

    const diffDays = parsedEnd.diff(parsedStart, 'day')
    const safeDiff = Number.isFinite(diffDays) ? Math.max(diffDays, 0) : 0
    const adjustedEndStr = maxAllowedEndDay.format('YYYYMMDD')
    const adjustedStartStr = dayjs(maxAllowedEndDay)
      .subtract(safeDiff, 'day')
      .format('YYYYMMDD')

    if (adjustedEndStr === endDate && adjustedStartStr === startDate) {
      return
    }

    updateEndDate(adjustedEndStr)
    updateStartDate(adjustedStartStr)
  }, [endDate, maxAllowedEndDay, startDate, updateEndDate, updateStartDate])

  const {
    data: roasResponse,
    isLoading,
    error,
  } = useSWR([
    `/troas/dayn/range`,
    { startDate, endDate, mediaSource, day_n: dayValue },
  ])
  const roasData = roasResponse?.data || []

  const handleDaySelectChange = (value) => {
    setSelectedDay(value)
  }

  const handleCustomDayInputChange = (event) => {
    const value = event.target.value
    if (value === '') {
      setCustomDayInput('')
      return
    }
    const numericValue = Number(value)
    if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 300) {
      return
    }
    setCustomDayInput(value)
  }

  const handleCustomDayApply = () => {
    if (customDayInput === '') return
    const numericValue = Number(customDayInput)
    if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 300) {
      return
    }
    setSelectedDay(String(Math.floor(numericValue)))
    setCustomDayInput('')
    setIsDaySelectOpen(false)
  }

  // Quick date range functions
  const setQuickRange = (days) => {
    const endDay = maxAllowedEndDay
    const start = endDay.subtract(days, 'day')
    updateEndDate(endDay.format('YYYYMMDD'))
    updateStartDate(start.format('YYYYMMDD'))
  }

  // Check if current date range matches a quick range
  const isQuickRangeActive = (days) => {
    const endDay = maxAllowedEndDay
    const start = endDay.subtract(days, 'day')
    const expectedStartDate = start.format('YYYYMMDD')
    const expectedEndDate = maxAllowedEndDateStr

    return startDate === expectedStartDate && endDate === expectedEndDate
  }

  // Define country order (same as ER page)
  const countryOrder = [
    'total',
    'BR',
    'JP',
    'MX',
    'DE',
    'US',
    'HK',
    'IT',
    'SA',
    'KR',
    'TW',
    'ES',
    'ZA',
    'ID',
    'GB',
    'FR',
    'TH',
    'AR',
    'CO',
    'SG',
    'MY',
    'CA',
    'AE',
  ]

  // Process data for country-based grouping
  const processedData = useMemo(() => {
    if (!roasData || roasData.length === 0) return []

    // Get unique countries from data
    const uniqueCountries = [...new Set(roasData.map((d) => d.country))]

    // Add 'total' as the first country
    const allCountries = [
      'total',
      ...uniqueCountries.filter((c) => c !== 'total'),
    ]

    // Sort countries based on the predefined order
    const sortedCountries = allCountries.sort((a, b) => {
      const indexA = countryOrder.indexOf(a)
      const indexB = countryOrder.indexOf(b)

      // If country not in order list, put it at the end
      if (indexA === -1 && indexB === -1) return a.localeCompare(b)
      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })

    return sortedCountries.map((country) => ({
      country,
    }))
  }, [roasData])

  // Define custom product order
  const productOrder = [
    '1', // Text
    '2', // Personal
    '3', // HD
    '6', // 2021
    '14', // JPSingle
    '17', // Gemoji
    '4', // Anim
    '7', // TG
    '5', // Maker
    '9', // StickTok
    '13', // HD2023
    '15', // Keyboard
    '11', // Neku-A
    '12', // Neku-iOS
    '16', // Neku(OC-A)
  ]

  const sortedProducts = useMemo(() => {
    const excludedProducts = [
      'total',
      'total1',
      'total1_1',
      'total1_2',
      'total2',
    ]
    return Products.filter(
      (product) => !excludedProducts.includes(product.code),
    ).sort((a, b) => {
      const indexA = productOrder.indexOf(a.code)
      const indexB = productOrder.indexOf(b.code)

      if (indexA === -1 && indexB === -1) return a.name.localeCompare(b.name)
      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })
  }, [])

  // 处理点击 pid=14, country=JP 的 cell
  const handlePid14CellClick = useCallback(() => {
    navigate(
      `/troas/day0-roas/14?day=${selectedDay}&media_source=${mediaSource}`,
    )
  }, [navigate, selectedDay, mediaSource])

  // Build columns
  const columns = useMemo(() => {
    // 确保 roasData 是数组
    const safeRoasData = Array.isArray(roasData) ? roasData : []
    const cols = [countryColumn, totalColumn(safeRoasData)]

    // Add columns for each PID
    sortedProducts.forEach((pid) => {
      cols.push(pidColumn(pid, safeRoasData, handlePid14CellClick))
    })

    return cols
  }, [roasData, sortedProducts, handlePid14CellClick])

  const getCountryTotalRoas = (country) => {
    const scopedData =
      country === 'total'
        ? roasData
        : roasData.filter((item) => item.country === country)
    if (!scopedData.length) return ''

    const totalRevenue = scopedData.reduce(
      (sum, item) => sum + item.total_revenue,
      0,
    )
    const totalCost = scopedData.reduce((sum, item) => sum + item.total_cost, 0)

    if (totalCost === 0) return ''
    return totalRevenue / totalCost
  }

  const getPidRoasValue = (country, pidCode) => {
    if (country === 'total') {
      const pidData = roasData.filter((item) => item.pid === Number(pidCode))
      if (!pidData.length) return ''

      const totalRevenue = pidData.reduce(
        (sum, item) => sum + item.total_revenue,
        0,
      )
      const totalCost = pidData.reduce((sum, item) => sum + item.total_cost, 0)

      if (totalCost === 0) return ''
      return totalRevenue / totalCost
    }

    // 对于 pid=14 等特殊情况，可能有多个 package_name，需要汇总所有数据
    const matchedData = roasData.filter(
      (item) => item.country === country && item.pid === Number(pidCode),
    )
    if (!matchedData.length) return ''

    // 如果有多条数据（多个 package_name），需要汇总计算 ROAS
    if (matchedData.length > 1) {
      const totalRevenue = matchedData.reduce(
        (sum, item) => sum + item.total_revenue,
        0,
      )
      const totalCost = matchedData.reduce(
        (sum, item) => sum + item.total_cost,
        0,
      )
      if (totalCost === 0) return ''
      return totalRevenue / totalCost
    }

    // 如果只有一条数据，直接返回
    return matchedData[0].avg_day0_roas ?? ''
  }

  const getRoasAsDecimalString = (value) =>
    typeof value === 'number' ? value.toFixed(4) : ''

  const handleExportCsv = () => {
    if (!roasData.length || !processedData.length) return

    const filename = `day${dayValue}_roas_${dayjs().format(
      'YYYYMMDDHHmmss',
    )}.csv`
    const headers = ['Country', 'Total', ...sortedProducts.map((p) => p.name)]
    const rows = processedData.map((row) => {
      const country = row.country
      const exportRow = {}

      exportRow.Country = country === 'total' ? 'Total' : country.toUpperCase()
      exportRow.Total = getRoasAsDecimalString(getCountryTotalRoas(country))

      sortedProducts.forEach((product) => {
        exportRow[product.name] = getRoasAsDecimalString(
          getPidRoasValue(country, product.code),
        )
      })

      return exportRow
    })

    exportToCsv(rows, filename, headers)
  }

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-4 flex-wrap sm:gap-2 sm:py-2 xs:py-1 xs:gap-1">
        <MyDatePicker
          datePickerStore={useDay0RoasDatePicker}
          getMaxDate="roas-complete"
          showMaxDate={true}
          defaultAllowIncomplete={false}
        />
        <DropdownMediaGroup value={mediaSource} update={setMediaSource} />
        <Select
          value={selectedDay}
          onValueChange={handleDaySelectChange}
          open={isDaySelectOpen}
          onOpenChange={setIsDaySelectOpen}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Day 0" />
          </SelectTrigger>
          <SelectContent>
            <div className="border-b border-muted p-3 space-y-2">
              <div className="text-xs text-muted-foreground">
                Custom Day (0-300)
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={300}
                  value={customDayInput}
                  placeholder="e.g. 45"
                  onChange={handleCustomDayInputChange}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      handleCustomDayApply()
                    }
                  }}
                  className="h-8"
                />
                <Button size="sm" onClick={handleCustomDayApply}>
                  Apply
                </Button>
              </div>
            </div>
            {selectOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button
            variant={isQuickRangeActive(6) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickRange(6)}
          >
            Past 7 Days
          </Button>
          <Button
            variant={isQuickRangeActive(13) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickRange(13)}
          >
            Past 14 Days
          </Button>
          <Button
            variant={isQuickRangeActive(29) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickRange(29)}
          >
            Past 30 Days
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCsv}>
          Export
        </Button>
      </div>

      {/* Data Table */}
      <div className="relative min-h-[256px]">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium">
            Loading day{dayValue} ROAS data...
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium text-red-600">
            Failed to load day{dayValue} ROAS data: {error?.message}
          </div>
        ) : (
          <DataTableWithBorder table={table} />
        )}
      </div>

      {/* Detail Dialog for PID 14 - Removed */}
    </div>
  )
}

export default Day0RoasPage
